import React, { useState, useEffect } from 'react';
import { MessageCircle, X, ChevronDown, ChevronUp, User, Activity, Settings } from 'lucide-react';

const BUSINESS_PHONE_NUMBER = "919003149989";

// ✅ MOVED OUTSIDE - This fixes the focus issue
const Section = ({ index, title, icon: Icon, children, activeSection, setActiveSection }) => {
  const isActive = activeSection === index;
  return (
    <div className="section">
      <button 
        type="button" 
        className={`section-header ${isActive ? 'active' : ''}`}
        onClick={() => setActiveSection(isActive ? -1 : index)}
      >
        <div className="section-title">
          <Icon size={18} />
          <span>{title}</span>
        </div>
        {isActive ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      <div className={`section-content ${isActive ? 'open' : ''}`}>
        {children}
      </div>
    </div>
  );
};

const EnquiryForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    enquiringFor: 'Yourself',
    relation: '',
    fullName: '',
    age: '',
    gender: '',
    contactNumber: '',
    alternativeContact: '',
    email: '',
    location: '',
    occupation: '',
    goals: {
      weightLoss: false,
      weightGain: false,
      pcos: false,
      diabetes: false,
      thyroid: false,
      cholesterol: false,
      hypertension: false,
      pregnancy: false,
      childNutrition: false,
      generalWellness: false,
    },
    otherGoal: '',
    height: '',
    currentWeight: '',
    waistCircumference: '',
    hipCircumference: '',
    medicalConditions: '',
    socialHabits: {
      alcohol: false,
      smoking: false,
      tobacco: false,
      none: false,
    },
    consultationMode: '',
    howDidYouHear: '',
    otherSource: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [activeSection, setActiveSection] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const nutritionGoals = [
    { key: 'weightLoss', label: 'Weight Loss' },
    { key: 'weightGain', label: 'Weight Gain' },
    { key: 'pcos', label: 'PCOS/PCOD' },
    { key: 'diabetes', label: 'Diabetes' },
    { key: 'thyroid', label: 'Thyroid' },
    { key: 'cholesterol', label: 'Cholesterol' },
    { key: 'hypertension', label: 'BP/Hypertension' },
    { key: 'pregnancy', label: 'Pregnancy' },
    { key: 'childNutrition', label: 'Child Nutrition' },
    { key: 'generalWellness', label: 'General Wellness' },
  ];

  const socialHabitOptions = [
    { key: 'alcohol', label: 'Drinking' },
    { key: 'smoking', label: 'Smoking' },
    { key: 'tobacco', label: 'Tobacco/Chewing' },
    { key: 'none', label: 'None' },
  ];

  const referralOptions = ["Google", "Instagram", "Facebook", "Referral", "Doctor", "Other"];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const validateForm = () => {
    const errors = {};
    if (!formData.fullName?.trim()) errors.fullName = 'Required';
    if (!formData.age || formData.age < 1) errors.age = 'Required';
    if (!formData.gender) errors.gender = 'Required';
    if (!formData.contactNumber?.trim()) errors.contactNumber = 'Required';
    if (!formData.alternativeContact?.trim()) errors.alternativeContact = 'Required';
    if (!formData.location?.trim()) errors.location = 'Required';
    if (!formData.occupation?.trim()) errors.occupation = 'Required';
    if (!formData.height) errors.height = 'Required';
    if (!formData.currentWeight) errors.currentWeight = 'Required';
    if (!formData.consultationMode) errors.consultationMode = 'Required';
    if (!formData.howDidYouHear) errors.howDidYouHear = 'Required';
    if (formData.howDidYouHear === 'Other' && !formData.otherSource?.trim()) {
      errors.otherSource = 'Please specify';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleCheckboxChange = (key) => {
    setFormData(prev => ({ 
      ...prev, 
      goals: { ...prev.goals, [key]: !prev.goals[key] } 
    }));
  };

  const handleSocialHabitChange = (key) => {
    setFormData(prev => {
      // If "None" is selected, clear all others. If any other is selected, clear "None".
      if (key === 'none') {
        return {
          ...prev,
          socialHabits: {
            alcohol: false,
            smoking: false,
            tobacco: false,
            none: !prev.socialHabits.none,
          },
        };
      }
      return {
        ...prev,
        socialHabits: {
          ...prev.socialHabits,
          [key]: !prev.socialHabits[key],
          none: false,
        },
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setActiveSection(0);
      return;
    }

    setIsSubmitting(true);

    const selectedGoals = Object.keys(formData.goals)
      .filter((key) => formData.goals[key])
      .map(key => key.replace(/([A-Z])/g, ' $1').trim());
    
    if (formData.otherGoal) selectedGoals.push(formData.otherGoal);

    const selectedHabits = socialHabitOptions
      .filter(opt => formData.socialHabits[opt.key])
      .map(opt => opt.label);

    const sourceText = formData.howDidYouHear === 'Other' && formData.otherSource
      ? formData.otherSource
      : formData.howDidYouHear;

    const message = 
`*📢 NEW ENQUIRY*
---
*Personal Details:*
Name: ${formData.fullName}
Age: ${formData.age} | ${formData.gender}
Phone: ${formData.contactNumber}
Alt. Contact: ${formData.alternativeContact || 'N/A'}
Email: ${formData.email || 'N/A'}
Location: ${formData.location}
Occupation: ${formData.occupation}
For: ${formData.enquiringFor} ${formData.relation ? `(${formData.relation})` : ''}

*Body Stats:*
Height: ${formData.height} cm | Weight: ${formData.currentWeight} kg
${formData.waistCircumference ? `Waist: ${formData.waistCircumference} cm` : ''}
${formData.hipCircumference ? `Hip: ${formData.hipCircumference} cm` : ''}

*Goals:* ${selectedGoals.join(', ') || 'General Health'}

*Medical:* ${formData.medicalConditions || 'None'}
*Social Habits:* ${selectedHabits.join(', ') || 'Not specified'}

*Consultation:* ${formData.consultationMode}
*Source:* ${sourceText}
---`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${BUSINESS_PHONE_NUMBER}?text=${encodedMessage}`;

    setTimeout(() => {
      window.open(whatsappUrl, '_blank');
      setIsSubmitting(false);
      onClose();
    }, 800);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="overlay" onClick={onClose}>
        <div className={`modal ${isMobile ? 'mobile' : 'desktop'}`} onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="header">
            <div>
              <h2>Book Consultation</h2>
              <p>Fill details & connect via WhatsApp</p>
            </div>
            <button className="close-btn" onClick={onClose} type="button">
              <X size={20} />
            </button>
          </div>

          {/* Scrollable Form Body */}
          <div className="form-scroll-wrapper">
            <form onSubmit={handleSubmit} className="form-body">
              
              {/* Section 1: Personal */}
              <Section 
                index={0} 
                title="Personal Details" 
                icon={User}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
              >
                <div className="field">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Your name"
                    className={formErrors.fullName ? 'error' : ''}
                  />
                </div>

                <div className="field-row">
                  <div className="field">
                    <label>Age *</label>
                    <input
                      type="number"
                      name="age"
                      value={formData.age}
                      onChange={handleChange}
                      placeholder="25"
                      className={formErrors.age ? 'error' : ''}
                    />
                  </div>
                  <div className="field">
                    <label>Gender *</label>
                    <div className="toggle-group">
                      {['Male', 'Female'].map(g => (
                        <button
                          key={g}
                          type="button"
                          className={`toggle-btn ${formData.gender === g ? 'active' : ''}`}
                          onClick={() => { 
                            setFormData(prev => ({...prev, gender: g})); 
                            setFormErrors(prev => ({...prev, gender: ''})); 
                          }}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="field-row">
                  <div className="field">
                    <label>WhatsApp *</label>
                    <input
                      type="tel"
                      name="contactNumber"
                      value={formData.contactNumber}
                      onChange={handleChange}
                      placeholder="9876543210"
                      className={formErrors.contactNumber ? 'error' : ''}
                    />
                  </div>
                  <div className="field">
                    <label>Alternative Contact</label>
                    <input
                      type="tel"
                      name="alternativeContact"
                      value={formData.alternativeContact}
                      onChange={handleChange}
                      placeholder="Alt. WhatsApp / Phone"
                    />
                    <span className="field-hint">Optional – another number we can reach you on</span>
                  </div>
                </div>

                <div className="field-row">
                  <div className="field">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="email@example.com"
                    />
                  </div>
                  <div className="field">
                    <label>City *</label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="Chennai"
                      className={formErrors.location ? 'error' : ''}
                    />
                  </div>
                </div>

                <div className="field">
                  <label>Occupation *</label>
                  <input
                    type="text"
                    name="occupation"
                    value={formData.occupation}
                    onChange={handleChange}
                    placeholder="Engineer"
                    className={formErrors.occupation ? 'error' : ''}
                  />
                </div>

                <div className="field">
                  <label>Enquiring For</label>
                  <div className="toggle-group">
                    {['Yourself', 'Someone else'].map(opt => (
                      <button
                        key={opt}
                        type="button"
                        className={`toggle-btn ${formData.enquiringFor === opt ? 'active' : ''}`}
                        onClick={() => setFormData(prev => ({...prev, enquiringFor: opt}))}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                {formData.enquiringFor === 'Someone else' && (
                  <div className="field">
                    <label>Relation</label>
                    <input
                      type="text"
                      name="relation"
                      value={formData.relation}
                      onChange={handleChange}
                      placeholder="e.g. Daughter, Mother"
                    />
                  </div>
                )}
              </Section>

              {/* Section 2: Health */}
              <Section 
                index={1} 
                title="Health & Goals" 
                icon={Activity}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
              >
                <div className="field-row">
                  <div className="field">
                    <label>Height (cm) *</label>
                    <input
                      type="number"
                      name="height"
                      value={formData.height}
                      onChange={handleChange}
                      placeholder="165"
                      className={formErrors.height ? 'error' : ''}
                    />
                  </div>
                  <div className="field">
                    <label>Weight (kg) *</label>
                    <input
                      type="number"
                      name="currentWeight"
                      value={formData.currentWeight}
                      onChange={handleChange}
                      placeholder="65"
                      className={formErrors.currentWeight ? 'error' : ''}
                    />
                  </div>
                </div>

                <div className="field">
                  <label>Health Goals</label>
                  <div className="chip-grid">
                    {nutritionGoals.map(goal => (
                      <button
                        key={goal.key}
                        type="button"
                        className={`chip ${formData.goals[goal.key] ? 'active' : ''}`}
                        onClick={() => handleCheckboxChange(goal.key)}
                      >
                        {goal.label}
                      </button>
                    ))}
                  </div>

                  {formData.goals.generalWellness && (
                    <input
                      type="text"
                      name="otherGoal"
                      value={formData.otherGoal}
                      onChange={handleChange}
                      placeholder="Tell us more about your wellness goal..."
                      style={{ marginTop: '8px' }}
                    />
                  )}
                </div>

                <div className="field">
                  <label>Medical Conditions</label>
                  <textarea
                    name="medicalConditions"
                    value={formData.medicalConditions}
                    onChange={handleChange}
                    placeholder="Any existing conditions..."
                    rows={2}
                  />
                </div>

                <div className="field">
                  <label>Social Habits</label>
                  <div className="chip-grid">
                    {socialHabitOptions.map(opt => (
                      <button
                        key={opt.key}
                        type="button"
                        className={`chip ${formData.socialHabits[opt.key] ? 'active' : ''}`}
                        onClick={() => handleSocialHabitChange(opt.key)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </Section>

              {/* Section 3: Preferences */}
              <Section 
                index={2} 
                title="Preferences" 
                icon={Settings}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
              >
                <div className="field">
                  <label>Consultation Mode *</label>
                  <div className="toggle-group">
                    {['Online', 'Offline'].map(mode => (
                      <button
                        key={mode}
                        type="button"
                        className={`toggle-btn ${formData.consultationMode === mode ? 'active' : ''} ${formErrors.consultationMode ? 'error-border' : ''}`}
                        onClick={() => { 
                          setFormData(prev => ({...prev, consultationMode: mode})); 
                          setFormErrors(prev => ({...prev, consultationMode: ''})); 
                        }}
                      >
                        {mode}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="field">
                  <label>How did you hear about us? *</label>
                  <div className="chip-grid">
                    {referralOptions.map(opt => (
                      <button
                        key={opt}
                        type="button"
                        className={`chip ${formData.howDidYouHear === opt ? 'active' : ''}`}
                        onClick={() => { 
                          setFormData(prev => ({...prev, howDidYouHear: opt})); 
                          setFormErrors(prev => ({...prev, howDidYouHear: ''})); 
                        }}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>

                  {formData.howDidYouHear === 'Other' && (
                    <input
                      type="text"
                      name="otherSource"
                      value={formData.otherSource}
                      onChange={handleChange}
                      placeholder="Please specify..."
                      className={formErrors.otherSource ? 'error' : ''}
                      style={{ marginTop: '8px' }}
                    />
                  )}
                </div>
              </Section>

              {/* Submit Button */}
              <button 
                type="submit" 
                className={`submit-btn ${isSubmitting ? 'loading' : ''}`}
                disabled={isSubmitting}
              >
                <MessageCircle size={20} />
                <span>{isSubmitting ? 'Opening WhatsApp...' : 'Send via WhatsApp'}</span>
              </button>

            </form>
          </div>
        </div>
      </div>

      <style jsx>{`
        * {
          box-sizing: border-box;
        }

        .overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .modal.desktop {
          width: 100%;
          max-width: 520px;
          max-height: calc(100vh - 40px);
          background: #fff;
          border-radius: 16px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          animation: scaleIn 0.3s ease;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }

        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        .modal.mobile {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          width: 100%;
          max-height: 90vh;
          background: #fff;
          border-radius: 20px 20px 0 0;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          animation: slideUp 0.3s ease;
          box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.2);
        }

        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }

        .header {
          padding: 18px 20px;
          background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: white;
          flex-shrink: 0;
        }

        .header h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
        }

        .header p {
          margin: 4px 0 0;
          font-size: 12px;
          opacity: 0.9;
        }

        .close-btn {
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: 0.2s;
          flex-shrink: 0;
        }

        .close-btn:hover {
          background: rgba(255,255,255,0.3);
        }

        .form-scroll-wrapper {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          -webkit-overflow-scrolling: touch;
        }

        .form-body {
          padding: 16px;
        }

        .section {
          margin-bottom: 10px;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          overflow: hidden;
          background: #fff;
        }

        .section-header {
          width: 100%;
          padding: 12px 14px;
          background: #f9fafb;
          border: none;
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          transition: 0.2s;
        }

        .section-header:hover {
          background: #f3f4f6;
        }

        .section-header.active {
          background: #ecfdf5;
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          font-size: 13px;
          color: #374151;
        }

        .section-content {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease, padding 0.3s ease;
          padding: 0 14px;
          background: white;
        }

        .section-content.open {
          max-height: 800px;
          padding: 14px;
          border-top: 1px solid #e5e7eb;
        }

        .field {
          margin-bottom: 12px;
        }

        .field:last-child {
          margin-bottom: 0;
        }

        .field label {
          display: block;
          font-size: 11px;
          font-weight: 600;
          color: #6b7280;
          margin-bottom: 5px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .field-hint {
          display: block;
          font-size: 11px;
          color: #9ca3af;
          margin-top: 4px;
          font-style: italic;
        }

        .field-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 12px;
        }

        .field-row .field {
          margin-bottom: 0;
        }

        input, textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1.5px solid #e5e7eb;
          border-radius: 8px;
          font-size: 14px;
          transition: 0.2s;
          background: #fff;
          font-family: inherit;
        }

        input:focus, textarea:focus {
          outline: none;
          border-color: #25D366;
          box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
        }

        input.error, textarea.error {
          border-color: #ef4444;
          background: #fef2f2;
        }

        textarea {
          resize: none;
          min-height: 60px;
        }

        .toggle-group {
          display: flex;
          gap: 8px;
        }

        .toggle-btn {
          flex: 1;
          padding: 9px 12px;
          border: 1.5px solid #e5e7eb;
          background: white;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: 0.2s;
          color: #374151;
          font-family: inherit;
        }

        .toggle-btn:hover {
          border-color: #25D366;
        }

        .toggle-btn.active {
          background: #ecfdf5;
          border-color: #25D366;
          color: #059669;
        }

        .toggle-btn.error-border {
          border-color: #ef4444;
        }

        .chip-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .chip {
          padding: 7px 12px;
          border: 1.5px solid #e5e7eb;
          background: white;
          border-radius: 18px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: 0.2s;
          color: #6b7280;
          font-family: inherit;
        }

        .chip:hover {
          border-color: #25D366;
          color: #059669;
        }

        .chip.active {
          background: #25D366;
          border-color: #25D366;
          color: white;
        }

        .submit-btn {
          width: 100%;
          padding: 14px 20px;
          background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
          border: none;
          border-radius: 10px;
          color: white;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 14px;
          transition: 0.2s;
          box-shadow: 0 4px 14px rgba(37, 211, 102, 0.35);
          font-family: inherit;
        }

        .submit-btn:hover:not(.loading) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(37, 211, 102, 0.4);
        }

        .submit-btn.loading {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .modal.mobile .field-row {
          grid-template-columns: 1fr;
          gap: 12px;
        }

        .modal.mobile input,
        .modal.mobile textarea {
          font-size: 16px;
        }

        .form-scroll-wrapper::-webkit-scrollbar {
          width: 6px;
        }

        .form-scroll-wrapper::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        .form-scroll-wrapper::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }
      `}</style>
    </>
  );
};

export default EnquiryForm;
